import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { useUser } from "@clerk/clerk-react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  ArrowRight,
  ArrowUpRight,
  Briefcase,
  Building2,
  Megaphone,
  SearchX,
} from "lucide-react";
import { raiseBoardApi } from "@/api/raise-board-api";
import { BoardAuthDialog } from "@/components/auth/board-auth-dialog";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { MultiSelect } from "@/components/ui/multi-select";
import { TooltipProvider } from "@/components/ui/tooltip";
import {
  CoverEyebrow,
  FundMark,
  PageContainer,
  HOUSE_COVER,
  PublicCover,
  PublicState,
  RaiseBoardNav,
  RaiseBoardShell,
} from "./rb-ui";
import {
  BoardCard,
  BoardCardSkeleton,
  BoardList,
  BoardRow,
  BoardRowSkeleton,
} from "./board-row";
import {
  BoardGrid,
  CollectionToolbar,
  SortSelect,
  ViewToggle,
  sortApplications,
  useSort,
  useViewMode,
} from "./board-collection";
import {
  getApplicantCompanyId,
  getFilterValues,
  getSource,
  matchesFilter,
  uniqueSorted,
  type Application,
} from "./deal-fields";
import { useMyVotes, useToggleUpvote } from "./use-raise-board-actions";

/** Orders the global list can take; the API's own is by endorsements. */
export default function RaiseBoardGlobal() {
  const navigate = useNavigate();
  const { isSignedIn } = useUser();

  const [industryFilter, setIndustryFilter] = useState<string[]>([]);
  const [businessModelFilter, setBusinessModelFilter] = useState<string[]>([]);
  const [sourceFilter, setSourceFilter] = useState<string[]>([]);
  const [sortBy, setSortBy] = useSort("endorsements");
  const [view, setView] = useViewMode("global");
  const [authReason, setAuthReason] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ["raise-board-global"],
    queryFn: () => raiseBoardApi.getGlobal(),
    retry: false,
    // Keep the live board fresh — picks up other users' votes and re-ranks
    // when the tab regains focus.
    refetchOnWindowFocus: true,
  });

  const votedIds = useMyVotes(!!isSignedIn);
  const toggleVote = useToggleUpvote();

  const voteState = (application: Application) => ({
    voted: votedIds.has(String(application._id)),
    count: application.upvote_count ?? 0,
  });

  const handleVote = (application: Application) => {
    if (!isSignedIn) {
      setAuthReason("Sign in to upvote this deal.");
      return;
    }
    toggleVote.mutate(String(application._id));
  };

  const openDeal = (application: Application) => {
    const slug = getSource(application).slug;
    const applicantId = getApplicantCompanyId(application);
    if (!slug || !applicantId) {
      toast.error("This deal's profile isn't available.");
      return;
    }
    navigate(`/rb/${slug}/${applicantId}`, { state: { from: "/global" } });
  };

  // Memoized so the `?? []` fallback doesn't hand a fresh array to every
  // derived useMemo on each render.
  const applications = useMemo(
    () => data?.result?.applications ?? [],
    [data]
  );

  const filterOptions = useMemo(() => {
    const industries: string[] = [];
    const businessModels: string[] = [];
    const sources: string[] = [];
    for (const app of applications) {
      const v = getFilterValues(app);
      industries.push(...v.industries);
      businessModels.push(...v.businessModels);
      if (v.source) sources.push(v.source);
    }
    return {
      industries: uniqueSorted(industries),
      businessModels: uniqueSorted(businessModels),
      sources: uniqueSorted(sources),
    };
  }, [applications]);

  // Boards feeding the global list, with the deal count each contributes.
  // Derived from the same payload — no extra request.
  const boards = useMemo(() => {
    const byName = new Map<
      string,
      { name: string; slug?: string; enabled?: boolean; count: number }
    >();
    for (const app of applications) {
      const s = getSource(app);
      if (!s.name) continue;
      const existing = byName.get(s.name);
      if (existing) existing.count += 1;
      else
        byName.set(s.name, {
          name: s.name,
          slug: s.slug,
          enabled: s.enabled,
          count: 1,
        });
    }
    return Array.from(byName.values()).sort((a, b) => b.count - a.count);
  }, [applications]);

  const filtered = useMemo(() => {
    return applications.filter((app) => {
      const v = getFilterValues(app);
      return (
        matchesFilter(v.industries, industryFilter) &&
        matchesFilter(v.businessModels, businessModelFilter) &&
        matchesFilter(v.source ? [v.source] : [], sourceFilter)
      );
    });
  }, [applications, industryFilter, businessModelFilter, sourceFilter]);

  // The API already ranks by endorsements, so that order is kept as is;
  // every other order is applied here over the filtered set.
  const sorted = useMemo(
    () => (sortBy === "endorsements" ? filtered : sortApplications(filtered, sortBy)),
    [filtered, sortBy]
  );

  const hasFilters =
    industryFilter.length > 0 ||
    businessModelFilter.length > 0 ||
    sourceFilter.length > 0;

  const toOptions = (values: string[]) =>
    values.map((v) => ({ label: v, value: v }));

  const authDialog = (
    <BoardAuthDialog
      open={!!authReason}
      onOpenChange={(open) => {
        if (!open) setAuthReason(null);
      }}
      reason={authReason ?? undefined}
    />
  );

  if (error) {
    return (
      <RaiseBoardShell width="wide">
        <RaiseBoardNav background={HOUSE_COVER} onRequestAuth={setAuthReason} width="wide" />
        <PublicCover
          background={HOUSE_COVER}
          width="wide"
          size="lg"
          eyebrow={
            <>
              <Megaphone />
              Raiseboard
            </>
          }
          title="The Raiseboard isn't available right now"
          lede="Something went wrong loading the board. Please try again in a moment."
        />
        {authDialog}
      </RaiseBoardShell>
    );
  }

  const companyWord = applications.length === 1 ? "startup" : "startups";

  return (
    <TooltipProvider delayDuration={200}>
      <RaiseBoardShell width="wide">
        <RaiseBoardNav background={HOUSE_COVER} onRequestAuth={setAuthReason} width="wide" />

        <PublicCover
          background={HOUSE_COVER}
          width="wide"
          eyebrow={
            <CoverEyebrow
              items={[
                "Raiseboard",
                <span className="inline-flex items-center gap-2">
                  <span className="relative flex h-1.5 w-1.5">
                    <span className="absolute inset-0 rounded-full bg-raising/40 motion-safe:animate-ping [animation-duration:2.4s]" />
                    <span className="relative h-1.5 w-1.5 rounded-full bg-raising" />
                  </span>
                  {isLoading
                    ? "Live"
                    : `Live across ${boards.length} ${boards.length === 1 ? "board" : "boards"}`}
                </span>,
              ]}
            />
          }
          title="Raiseboard"
          lede={
            isLoading ? (
              <Skeleton className="h-5 w-80" />
            ) : (
              <>
                <strong>
                  {applications.length} {companyWord}
                </strong>{" "}
                raising right now, ranked by endorsements from the investors
                who follow them.
              </>
            )
          }
          aside={
            !isSignedIn && (
              <>
                <p className="text-[13px] text-muted-foreground sm:text-right">
                  Endorse deals and follow the boards you care about.
                </p>
                <Button
                  onClick={() =>
                    setAuthReason("Create an account to join the Raiseboard.")
                  }
                  className="h-10 gap-1.5 rounded-lg px-4 text-[13.5px]"
                >
                  Join free
                  <ArrowRight className="h-3.5 w-3.5" />
                </Button>
              </>
            )
          }
        />

        <PageContainer width="wide" className="pt-10 sm:pt-12">
          <div className="flex flex-col gap-14 lg:flex-row lg:items-start lg:gap-16">
            <section aria-label="Companies" className="min-w-0 flex-1">
              <CollectionToolbar
                title="Raising now"
                count={isLoading ? undefined : sorted.length}
                sort={<SortSelect value={sortBy} onChange={setSortBy} />}
                view={<ViewToggle value={view} onChange={setView} />}
                filters={
                  <>
                    <MultiSelect
                      id="global-industry"
                      options={toOptions(filterOptions.industries)}
                      value={industryFilter}
                      onValueChange={setIndustryFilter}
                      placeholder="Industry"
                      triggerIcon={Building2}
                      maxCount={2}
                      className="h-9 w-auto min-w-[140px] rounded-lg border-border bg-card text-[13px]"
                    />
                    <MultiSelect
                      id="global-business-model"
                      options={toOptions(filterOptions.businessModels)}
                      value={businessModelFilter}
                      onValueChange={setBusinessModelFilter}
                      placeholder="Business model"
                      triggerIcon={Briefcase}
                      maxCount={2}
                      className="h-9 w-auto min-w-[160px] rounded-lg border-border bg-card text-[13px]"
                    />
                    <MultiSelect
                      id="global-source"
                      options={toOptions(filterOptions.sources)}
                      value={sourceFilter}
                      onValueChange={setSourceFilter}
                      placeholder="Raiseboard"
                      triggerIcon={Megaphone}
                      maxCount={2}
                      className="h-9 w-auto min-w-[140px] rounded-lg border-border bg-card text-[13px]"
                    />
                    {hasFilters && (
                      <span className="pl-1 text-[13px] tabular-nums text-muted-foreground">
                        {filtered.length} of {applications.length}
                      </span>
                    )}
                  </>
                }
              />

              {isLoading ? (
                view === "grid" ? (
                  <BoardGrid className="lg:grid-cols-2">
                    {[0, 1, 2, 3].map((i) => (
                      <BoardCardSkeleton key={i} />
                    ))}
                  </BoardGrid>
                ) : (
                  <BoardList>
                    {[0, 1, 2, 3, 4].map((i) => (
                      <BoardRowSkeleton key={i} />
                    ))}
                  </BoardList>
                )
              ) : applications.length === 0 ? (
                <PublicState
                  icon={<Megaphone />}
                  title="No one is raising here yet"
                  className="border-t border-foreground"
                >
                  Boards publish their deals here as they go live. Check back
                  soon.
                </PublicState>
              ) : filtered.length === 0 ? (
                <PublicState
                  icon={<SearchX />}
                  title="No companies match those filters"
                  className="border-t border-foreground"
                  action={
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-9 rounded-lg bg-card"
                      onClick={() => {
                        setIndustryFilter([]);
                        setBusinessModelFilter([]);
                        setSourceFilter([]);
                      }}
                    >
                      Clear filters
                    </Button>
                  }
                />
              ) : view === "grid" ? (
                <BoardGrid className="lg:grid-cols-2">
                  {sorted.map((application, index) => (
                    <BoardCard
                      key={String(application._id)}
                      application={application}
                      rank={index + 1}
                      index={index}
                      vote={voteState(application)}
                      onVote={() => handleVote(application)}
                      onOpen={() => openDeal(application)}
                    />
                  ))}
                </BoardGrid>
              ) : (
                <BoardList>
                  {sorted.map((application, index) => (
                    <BoardRow
                      key={String(application._id)}
                      application={application}
                      rank={index + 1}
                      index={index}
                      vote={voteState(application)}
                      onVote={() => handleVote(application)}
                      onOpen={() => openDeal(application)}
                    />
                  ))}
                </BoardList>
              )}
            </section>

            {/* Where these deals come from: a quiet index of the boards
                feeding the feed, so a visitor can jump to one. */}
            <aside className="w-full shrink-0 lg:sticky lg:top-24 lg:w-[280px]">
              <h2 className="pb-4 text-[20px] font-semibold tracking-[-0.025em]">
                Boards
                {!isLoading && (
                  <span className="ml-2 align-top text-[11px] font-medium tabular-nums tracking-normal text-muted-foreground">
                    {String(boards.length).padStart(2, "0")}
                  </span>
                )}
              </h2>
              {isLoading ? (
                <div className="space-y-3 border-t border-foreground pt-4">
                  <Skeleton className="h-7 w-full" />
                  <Skeleton className="h-7 w-full" />
                  <Skeleton className="h-7 w-4/5" />
                </div>
              ) : boards.length === 0 ? (
                <p className="mt-4 text-sm text-muted-foreground">
                  No boards are publishing to the global feed yet.
                </p>
              ) : (
                <ul className="divide-y divide-border border-y border-b-border border-t-foreground">
                  {boards.slice(0, 6).map((board) => {
                    const linked = board.slug && board.enabled;
                    const inner = (
                      <>
                        <FundMark name={board.name} size="md" />
                        <span className="min-w-0 flex-1 truncate text-[14.5px] font-medium tracking-[-0.01em]">
                          {board.name}
                        </span>
                        <span className="text-[13px] tabular-nums text-muted-foreground">
                          {board.count}
                        </span>
                        {linked && (
                          <ArrowUpRight className="h-3.5 w-3.5 text-muted-foreground opacity-0 transition-opacity duration-200 group-hover:opacity-100" />
                        )}
                      </>
                    );
                    return (
                      <li key={board.name}>
                        {linked ? (
                          <a
                            href={`/rb/${board.slug}`}
                            target="_blank"
                            rel="noreferrer"
                            className="group flex items-center gap-3 py-3 transition-transform duration-300 ease-out hover:translate-x-1"
                          >
                            {inner}
                          </a>
                        ) : (
                          <div className="flex items-center gap-3 py-3">
                            {inner}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
              <div className="mt-5 flex flex-col items-start gap-3">
                {/* The rail shows the six biggest contributors; the directory
                    is where every published board lives. */}
                <button
                  type="button"
                  onClick={() => navigate("/rb")}
                  className="inline-flex items-center gap-1.5 text-[13px] font-medium transition-colors hover:text-muted-foreground"
                >
                  See all boards
                  <ArrowRight className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() =>
                    isSignedIn
                      ? navigate("/workspaces")
                      : setAuthReason("Sign in to run your own Raiseboard.")
                  }
                  className="inline-flex items-center gap-1.5 text-[13px] text-muted-foreground transition-colors hover:text-foreground"
                >
                  Run your own board
                  <ArrowRight className="h-3.5 w-3.5" />
                </button>
              </div>
            </aside>
          </div>
        </PageContainer>

        {authDialog}
      </RaiseBoardShell>
    </TooltipProvider>
  );
}
